import { useEffect, useMemo, useState } from "react";
import { Calendar, Pill, Plus, X } from "lucide-react";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import medicationService from "../services/MedicationService";
import { measureSectionTitle } from "../utils/measurePageTypography";
import { cn } from "../lib/utils";

export type MedicationAddSheetProps = {
  open: boolean;
  onClose: () => void;
  initialName?: string;
  onSaved?: () => void;
};

const WEEKDAYS = [
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
  { key: "sun", label: "S" },
] as const;

type EndsType = "never" | "on_date" | "after_doses";

const DEFAULT_REPEAT_DAYS = ["mon", "tue", "wed", "thu", "fri"];

const MEDICATION_NAME_MIN = 2;
const MEDICATION_NAME_MAX = 80;
const MEDICATION_FREQUENCY_MIN = 2;
const MEDICATION_FREQUENCY_MAX = 100;
const MEDICATION_TIME_PLACEHOLDER = "e.g., 08:00";

function clampText(value: string, max: number): string {
  return value.slice(0, max);
}

/** Single medication sheet theme — sky/blue accent, one border style everywhere */
const medFieldClass =
  "h-12 w-full rounded-xl border border-border bg-background px-4 text-base font-normal text-foreground placeholder:font-normal placeholder:text-muted-foreground/35 focus:border-blue-500 focus:outline-none focus:ring-0";

const medFieldErrorClass =
  "border-red-400 focus:border-red-500 dark:border-red-500/70";

const medErrorTextClass = "mt-1.5 text-xs font-medium text-red-600 dark:text-red-400";

const medLabelClass =
  "mb-2 block text-2xs font-semibold uppercase tracking-[0.1em] text-muted-foreground";

const chipActiveClass =
  "border-transparent bg-gradient-primary text-primary-foreground shadow-primary";

const chipIdleClass =
  "border-border bg-background text-foreground hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-950/20";

const DAY_NAMES: Record<(typeof WEEKDAYS)[number]["key"], string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function getRepeatSummary(days: string[]): string {
  const selected = new Set(days);
  const isWeekdays =
    days.length === 5 && DEFAULT_REPEAT_DAYS.every((d) => selected.has(d));
  const isEveryDay = days.length === 7;
  if (isEveryDay) return "Every day";
  if (isWeekdays) return "Mon, Tue, Wed, Thu, Fri";
  if (days.length === 0) return "Select days";

  const names = WEEKDAYS.filter((d) => selected.has(d.key)).map(
    (d) => DAY_NAMES[d.key],
  );
  return names.join(", ");
}

type FieldErrors = {
  name?: string;
  frequency?: string;
  times?: string;
  timeRows?: Record<number, string>;
  repeatDays?: string;
  endDate?: string;
  doseCount?: string;
};

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className={medErrorTextClass} role="alert">
      {message}
    </p>
  );
}

const TIME_RE = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

function validateMedicationForm(input: {
  name: string;
  frequency: string;
  times: string[];
  repeatEnabled: boolean;
  repeatDays: string[];
  endsType: EndsType;
  endDate: string;
  doseCount: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  const trimmedName = input.name.trim();
  const trimmedFrequency = input.frequency.trim();

  if (!trimmedName) {
    errors.name = "Medication name is required.";
  } else if (trimmedName.length < MEDICATION_NAME_MIN) {
    errors.name = `Enter at least ${MEDICATION_NAME_MIN} characters.`;
  } else if (trimmedName.length > MEDICATION_NAME_MAX) {
    errors.name = `Name is too long (max ${MEDICATION_NAME_MAX} characters).`;
  }

  if (!trimmedFrequency) {
    errors.frequency = "Frequency / dose is required.";
  } else if (trimmedFrequency.length < MEDICATION_FREQUENCY_MIN) {
    errors.frequency =
      "Enter dose or when you take it (e.g. 500 mg · With breakfast).";
  } else if (trimmedFrequency.length > MEDICATION_FREQUENCY_MAX) {
    errors.frequency = `Too long (max ${MEDICATION_FREQUENCY_MAX} characters).`;
  }

  const timeRows: Record<number, string> = {};
  const filledTimes: string[] = [];
  input.times.forEach((raw, index) => {
    const time = raw.trim();
    if (!time) {
      if (input.times.length === 1 || input.times.some((t) => t.trim())) {
        timeRows[index] = "Select a time.";
      }
      return;
    }
    if (!TIME_RE.test(time)) {
      timeRows[index] = "Enter a valid time.";
      return;
    }
    if (filledTimes.includes(time)) {
      timeRows[index] = "This time is already added.";
      return;
    }
    filledTimes.push(time);
  });

  if (filledTimes.length === 0) {
    errors.times = "Add at least one time.";
  }
  if (Object.keys(timeRows).length > 0) {
    errors.timeRows = timeRows;
  }

  if (input.repeatEnabled) {
    if (input.repeatDays.length === 0) {
      errors.repeatDays = "Select at least one day.";
    }
    if (input.endsType === "on_date") {
      if (!input.endDate) {
        errors.endDate = "End date is required.";
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const picked = new Date(`${input.endDate}T00:00:00`);
        if (Number.isNaN(picked.getTime())) {
          errors.endDate = "Enter a valid date.";
        } else if (picked < today) {
          errors.endDate = "End date cannot be in the past.";
        }
      }
    }
    if (input.endsType === "after_doses") {
      const n = Number(input.doseCount);
      if (!input.doseCount.trim()) {
        errors.doseCount = "Number of doses is required.";
      } else if (!Number.isInteger(n) || n < 1) {
        errors.doseCount = "Enter a whole number of 1 or more.";
      } else if (n > 999) {
        errors.doseCount = "Maximum 999 doses.";
      }
    }
  }

  return errors;
}

function hasValidationErrors(errors: FieldErrors): boolean {
  return Boolean(
    errors.name ||
      errors.frequency ||
      errors.times ||
      errors.repeatDays ||
      errors.endDate ||
      errors.doseCount ||
      (errors.timeRows && Object.keys(errors.timeRows).length > 0),
  );
}

function markAllFieldsTouched(timesLength: number): Record<string, boolean> {
  const touched: Record<string, boolean> = {
    name: true,
    frequency: true,
    repeatDays: true,
    endDate: true,
    doseCount: true,
  };
  for (let i = 0; i < timesLength; i++) {
    touched[`time-${i}`] = true;
  }
  return touched;
}

const MedicationAddSheet = ({
  open,
  onClose,
  initialName = "",
  onSaved,
}: MedicationAddSheetProps) => {
  const [name, setName] = useState("");
  const [frequency, setFrequency] = useState("");
  const [times, setTimes] = useState<string[]>([""]);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatDays, setRepeatDays] = useState<string[]>(DEFAULT_REPEAT_DAYS);
  const [endsType, setEndsType] = useState<EndsType>("never");
  const [endDate, setEndDate] = useState("");
  const [doseCount, setDoseCount] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const markTouched = (key: string) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const fieldErrors = useMemo(
    () =>
      validateMedicationForm({
        name,
        frequency,
        times,
        repeatEnabled,
        repeatDays,
        endsType,
        endDate,
        doseCount,
      }),
    [name, frequency, times, repeatEnabled, repeatDays, endsType, endDate, doseCount],
  );

  const shouldShow = (key: string) => showErrors || touched[key];

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setFrequency("");
    setTimes([""]);
    setRepeatEnabled(false);
    setRepeatDays(DEFAULT_REPEAT_DAYS);
    setEndsType("never");
    setEndDate("");
    setDoseCount("");
    setError(null);
    setShowErrors(false);
    setTouched({});
  }, [open, initialName]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const validTimes = useMemo(
    () => times.map((t) => t.trim()).filter(Boolean),
    [times],
  );

  const isFormValid = !hasValidationErrors(fieldErrors);

  const repeatSummary = repeatEnabled
    ? getRepeatSummary(repeatDays)
    : "Does not repeat";

  const canSave = isFormValid && !saving;

  const updateTime = (index: number, value: string) => {
    setTimes((prev) => prev.map((t, i) => (i === index ? value : t)));
    if (value.trim()) {
      markTouched(`time-${index}`);
    }
  };

  const addAnotherTime = () => {
    const lastIndex = times.length - 1;
    if (!times[lastIndex]?.trim()) {
      setShowErrors(true);
      setTouched((prev) => ({ ...prev, [`time-${lastIndex}`]: true }));
      return;
    }
    setTimes((prev) => [...prev, ""]);
  };

  const removeTime = (index: number) => {
    setTimes((prev) => {
      if (prev.length <= 1) return [""];
      return prev.filter((_, i) => i !== index);
    });
  };

  const toggleRepeatDay = (day: string) => {
    markTouched("repeatDays");
    setRepeatDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const handleSave = async () => {
    setShowErrors(true);
    setTouched(markAllFieldsTouched(times.length));
    if (!isFormValid || saving) return;
    setSaving(true);
    setError(null);
    try {
      const repeatPayload = repeatEnabled
        ? {
            repeat_enabled: true,
            repeat_days: repeatDays,
            repeat_end_type: endsType,
            ...(endsType === "on_date" ? { end_date: endDate } : {}),
            ...(endsType === "after_doses"
              ? { number_of_doses: Number(doseCount) }
              : {}),
          }
        : { repeat_enabled: false };

      await medicationService.addMedicationTimes({
        medication_name: name.trim(),
        times: validTimes,
        frequency: frequency.trim(),
        ...repeatPayload,
      });

      window.dispatchEvent(new CustomEvent("medicationsUpdated"));
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to add medication";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[250] flex flex-col justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="relative z-10 mx-auto w-full max-w-[400px] animate-in slide-in-from-bottom-4 duration-200">
        <div className="flex max-h-[min(92dvh,820px)] flex-col overflow-hidden rounded-t-3xl border border-border bg-background shadow-2xl">
          {/* Header */}
          <div className="shrink-0 border-b border-border/60 bg-gradient-to-b from-sky-50/80 to-background px-5 pb-4 pt-3 dark:from-sky-950/25">
            <div className="mb-3 flex justify-center">
              <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
            </div>
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-sky-200 bg-sky-100 dark:border-sky-800/50 dark:bg-sky-950/40">
                  <Pill className="h-5 w-5 text-sky-600 dark:text-sky-400" strokeWidth={1.85} />
                </span>
                <div>
                  <p className="text-2xs font-semibold uppercase tracking-[0.12em] text-sky-700/80 dark:text-sky-300/80">
                    Medication · Log now
                  </p>
                  <h2 className={cn(measureSectionTitle, "mt-0.5")}>Add medication</h2>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/60"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Single form card */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-hide px-5 py-4">
            {showErrors && !isFormValid ? (
              <p
                className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
                role="alert"
              >
                Please complete all required fields below.
              </p>
            ) : null}
            <div className="space-y-4 rounded-2xl border border-border bg-card p-4 shadow-sm">
              <div>
                <Label className={medLabelClass}>
                  Medication name <span className="text-red-500">*</span>
                </Label>
                <input
                  type="text"
                  placeholder="e.g., Metformin"
                  value={name}
                  maxLength={MEDICATION_NAME_MAX}
                  onChange={(e) => setName(clampText(e.target.value, MEDICATION_NAME_MAX))}
                  onBlur={() => markTouched("name")}
                  aria-invalid={shouldShow("name") && Boolean(fieldErrors.name)}
                  className={cn(
                    medFieldClass,
                    shouldShow("name") && fieldErrors.name && medFieldErrorClass,
                  )}
                />
                {shouldShow("name") && fieldErrors.name ? (
                  <FieldError message={fieldErrors.name} />
                ) : (
                  <p className="mt-1 text-2xs text-muted-foreground">
                    {name.length}/{MEDICATION_NAME_MAX} characters
                  </p>
                )}
              </div>

              <div>
                <Label className={medLabelClass}>
                  Frequency / dose <span className="text-red-500">*</span>
                </Label>
                <input
                  type="text"
                  placeholder="e.g., 500 mg · With breakfast"
                  value={frequency}
                  maxLength={MEDICATION_FREQUENCY_MAX}
                  onChange={(e) =>
                    setFrequency(clampText(e.target.value, MEDICATION_FREQUENCY_MAX))
                  }
                  onBlur={() => markTouched("frequency")}
                  aria-invalid={shouldShow("frequency") && Boolean(fieldErrors.frequency)}
                  className={cn(
                    medFieldClass,
                    shouldShow("frequency") && fieldErrors.frequency && medFieldErrorClass,
                  )}
                />
                {shouldShow("frequency") && fieldErrors.frequency ? (
                  <FieldError message={fieldErrors.frequency} />
                ) : (
                  <p className="mt-1 text-2xs text-muted-foreground">
                    Dose (500 mg) or meal timing · {frequency.length}/
                    {MEDICATION_FREQUENCY_MAX} characters
                  </p>
                )}
              </div>

              <div>
                <Label className={medLabelClass}>
                  Time <span className="text-red-500">*</span>
                </Label>
                <div className="space-y-2">
                  {times.map((timeValue, index) => (
                    <div key={`time-${index}`}>
                      <div className="flex items-center gap-2">
                        <div className="relative min-w-0 flex-1">
                          <input
                            type="time"
                            value={timeValue}
                            onChange={(e) => updateTime(index, e.target.value)}
                            onBlur={() => markTouched(`time-${index}`)}
                            aria-invalid={
                              shouldShow(`time-${index}`) &&
                              Boolean(fieldErrors.timeRows?.[index])
                            }
                            className={cn(
                              medFieldClass,
                              !timeValue &&
                                "text-transparent [&::-webkit-datetime-edit]:opacity-0",
                              shouldShow(`time-${index}`) &&
                                fieldErrors.timeRows?.[index] &&
                                medFieldErrorClass,
                            )}
                          />
                          {!timeValue ? (
                            <span
                              aria-hidden
                              className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-base font-normal text-muted-foreground/35"
                            >
                              {MEDICATION_TIME_PLACEHOLDER}
                            </span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeTime(index)}
                          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/20"
                          aria-label="Remove time"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {shouldShow(`time-${index}`) && fieldErrors.timeRows?.[index] ? (
                        <FieldError message={fieldErrors.timeRows[index]} />
                      ) : null}
                    </div>
                  ))}
                  {showErrors && fieldErrors.times ? (
                    <FieldError message={fieldErrors.times} />
                  ) : null}
                  <button
                    type="button"
                    onClick={addAnotherTime}
                    className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-blue-300/80 bg-blue-50/40 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50 dark:border-blue-800/50 dark:bg-blue-950/20 dark:text-blue-300"
                  >
                    <Plus className="h-4 w-4" />
                    Add another time
                  </button>
                </div>
              </div>

              <div className="border-t border-border/60 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-foreground">Repeat</p>
                    <p className="text-xs text-muted-foreground">{repeatSummary}</p>
                  </div>
                  <Switch
                    checked={repeatEnabled}
                    onCheckedChange={(checked) => {
                      setRepeatEnabled(checked);
                      if (checked) markTouched("repeatDays");
                    }}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>

                {repeatEnabled ? (
                  <div className="mt-4 space-y-4">
                    <div>
                      <Label className={medLabelClass}>Repeat on</Label>
                      <div className="grid grid-cols-7 gap-1.5">
                        {WEEKDAYS.map((day) => {
                          const active = repeatDays.includes(day.key);
                          return (
                            <button
                              key={day.key}
                              type="button"
                              onClick={() => toggleRepeatDay(day.key)}
                              className={cn(
                                "flex h-9 items-center justify-center rounded-lg border text-sm font-bold transition-colors",
                                active ? chipActiveClass : chipIdleClass,
                              )}
                            >
                              {day.label}
                            </button>
                          );
                        })}
                      </div>
                      {shouldShow("repeatDays") && fieldErrors.repeatDays ? (
                        <FieldError message={fieldErrors.repeatDays} />
                      ) : null}
                    </div>

                    <div>
                      <Label className={medLabelClass}>Ends</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {(
                          [
                            { key: "never", label: "Never" },
                            { key: "on_date", label: "On date" },
                            { key: "after_doses", label: "After N doses" },
                          ] as const
                        ).map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            onClick={() => setEndsType(option.key)}
                            className={cn(
                              "rounded-xl border px-2 py-2.5 text-center text-xs font-semibold leading-tight transition-colors",
                              endsType === option.key ? chipActiveClass : chipIdleClass,
                            )}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {endsType === "on_date" ? (
                      <div>
                        <Label className={medLabelClass}>End date</Label>
                        <div className="relative">
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            onBlur={() => markTouched("endDate")}
                            aria-invalid={shouldShow("endDate") && Boolean(fieldErrors.endDate)}
                            className={cn(
                              medFieldClass,
                              "pr-10",
                              shouldShow("endDate") && fieldErrors.endDate && medFieldErrorClass,
                            )}
                          />
                          <Calendar className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sky-600/70 dark:text-sky-400/70" />
                        </div>
                        {shouldShow("endDate") && fieldErrors.endDate ? (
                          <FieldError message={fieldErrors.endDate} />
                        ) : null}
                      </div>
                    ) : null}

                    {endsType === "after_doses" ? (
                      <div>
                        <Label className={medLabelClass}>Number of doses</Label>
                        <input
                          type="number"
                          min={1}
                          placeholder="e.g., 30"
                          value={doseCount}
                          onChange={(e) => setDoseCount(e.target.value)}
                          onBlur={() => markTouched("doseCount")}
                          aria-invalid={shouldShow("doseCount") && Boolean(fieldErrors.doseCount)}
                          className={cn(
                            medFieldClass,
                            shouldShow("doseCount") && fieldErrors.doseCount && medFieldErrorClass,
                          )}
                        />
                        {shouldShow("doseCount") && fieldErrors.doseCount ? (
                          <FieldError message={fieldErrors.doseCount} />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {error ? (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
            ) : null}
          </div>

          {/* Footer */}
          <div className="shrink-0 border-t border-border/60 bg-background px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom,0.75rem))]">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
                className={cn(
                  "h-12 rounded-xl text-sm font-bold transition-colors",
                  canSave
                    ? "bg-gradient-primary text-primary-foreground shadow-primary hover:opacity-90 disabled:opacity-60"
                    : "border border-border bg-muted/50 text-muted-foreground hover:bg-muted/70",
                )}
              >
                {saving ? "Saving…" : "Add medication"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="h-12 rounded-xl border border-border bg-background text-sm font-semibold text-foreground transition-colors hover:bg-muted/40"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicationAddSheet;
